import { useMemo } from 'react'
import { useActivity, type ActivityTask } from '@/hooks/use-activity'
import { TASK_LABELS } from '@/api/activity-config'
import { Loader2 } from 'lucide-react'

interface EntityActivityBadgeProps {
  entityType: string
  entityId: number
}

function ActivityBadgeList({ tasks, variant = 'badge' }: { tasks: ActivityTask[]; variant?: 'badge' | 'text' }) {
  if (tasks.length === 0) return null

  if (variant === 'text') {
    // Quiet in-line progress: a spinner and the task in blue, no box.
    return (
      <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs font-medium text-blue-700 dark:text-blue-300">
        {tasks.map((task) => (
          <span key={task.uuid} className="inline-flex items-center gap-1">
            <Loader2 aria-hidden="true" className="size-3 animate-spin" />
            {task.entity_label || TASK_LABELS[task.name] || task.name}
            {task.progress != null && <span className="tabular-nums">{task.progress}%</span>}
          </span>
        ))}
      </span>
    )
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {tasks.map((task) => (
        <span
          key={task.uuid}
          className="inline-flex animate-pulse items-center gap-1.5 rounded-md border border-blue-300 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 shadow-sm shadow-blue-200/50 dark:border-blue-700 dark:bg-blue-950/50 dark:text-blue-300 dark:shadow-blue-900/30"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {task.entity_label || TASK_LABELS[task.name] || task.name}
          {task.progress != null && (
            <span className="tabular-nums text-blue-500">{task.progress}%</span>
          )}
        </span>
      ))}
    </div>
  )
}

export function EntityActivityBadge({ entityType, entityId }: EntityActivityBadgeProps) {
  const { activeTasks } = useActivity()

  const matching = activeTasks.filter(
    (t) => t.entity_type === entityType && t.entity_id === entityId,
  )

  return <ActivityBadgeList tasks={matching} />
}

interface ProjectActivityBadgesProps {
  orderIds: number[]
  communicationThreadIds: number[]
  variant?: 'badge' | 'text'
}

/** A project's order and email work in flight; each PO shows its own (PurchaseOrderActivity). */
export function ProjectActivityBadges({
  orderIds,
  communicationThreadIds,
  variant,
}: ProjectActivityBadgesProps) {
  const { activeTasks } = useActivity()

  const matching = useMemo(() => {
    const orderSet = new Set(orderIds)
    const commSet = new Set(communicationThreadIds)

    return activeTasks.filter((t) => {
      if (!t.entity_type || t.entity_id == null) return false
      switch (t.entity_type) {
        case 'order':
          return orderSet.has(t.entity_id)
        case 'communication':
          return commSet.has(t.entity_id)
        default:
          return false
      }
    })
  }, [activeTasks, orderIds, communicationThreadIds])

  return <ActivityBadgeList tasks={matching} variant={variant} />
}

/** What is in flight for one PO (its emails, its shipments), as quiet text under its statuses. */
export function PurchaseOrderActivity({
  purchaseOrderId,
  shipmentIds,
}: {
  purchaseOrderId: number
  shipmentIds: number[]
}) {
  const { activeTasks } = useActivity()
  const matching = activeTasks.filter(
    (t) =>
      t.entity_id != null &&
      ((t.entity_type === 'purchase_order' && t.entity_id === purchaseOrderId) ||
        (t.entity_type === 'shipment' && shipmentIds.includes(t.entity_id))),
  )
  return <ActivityBadgeList tasks={matching} variant="text" />
}
